import React from "react";

import {
  ProductCard as UIProductCard,
  type ProductCardProps as UIProductCardProps,
} from "@bared/ui";

import type { ProductCardContext } from "types/product";

import {
  ProductCardVisibilityObserver,
  ProductCardVisibilityObserverProps,
} from "~/components/ProductCardVisibilityObserver";

import { useCartAdd } from "~/hooks/useCart";
import { useWishlistToggle } from "~/hooks/useWishlist";

export type ProductCardProps = ProductCardContext &
  Omit<
    UIProductCardProps,
    "isInWishlist" | "onWishlistToggle" | "isAddingToCart"
  > &
  Omit<ProductCardVisibilityObserverProps, "children"> & {
    enableWishlist?: boolean;
    enableAddToCart?: boolean;
  };

export function ProductCard({
  index,
  initialInView,
  googleAnalyticsItem,
  firstAvailableVariantId,
  enableWishlist = true,
  enableAddToCart = false,
  ...props
}: ProductCardProps) {
  const { toggleWishlist, isInWishlist } = useWishlistToggle({
    googleAnalyticsItem,
    productHandle: props.handle,
  });

  const { isPending, addToCart } = useCartAdd();

  return (
    <ProductCardVisibilityObserver
      index={index}
      initialInView={initialInView}
      googleAnalyticsItem={googleAnalyticsItem}
    >
      {({ ref, onClick }) => (
        <UIProductCard
          {...props}
          ref={ref}
          onClick={onClick}
          {...(enableWishlist
            ? { isInWishlist, onWishlistToggle: toggleWishlist }
            : {})}
          {...(enableAddToCart && !!firstAvailableVariantId
            ? {
                isAddingToCart: isPending,
                onAddToCart: async () => {
                  if (!firstAvailableVariantId) return;
                  await addToCart({ merchandiseId: firstAvailableVariantId });
                  if (props.onAddToCart) props.onAddToCart();
                },
              }
            : {})}
        />
      )}
    </ProductCardVisibilityObserver>
  );
}
